import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { Download, Activity, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { useFEV1Trends } from '../../lib/hooks/useTrends';

interface FEV1TrendsCardProps {
  patientId: string;
  patientName: string;
}

export function FEV1TrendsCard({ patientId, patientName }: FEV1TrendsCardProps) {
  const [timeRange, setTimeRange] = useState('6weeks');
  
  // Get weeks from timeRange
  const getWeeks = (range: string) => {
    if (range === '12weeks') return 12;
    if (range === '6months') return 24;
    return 6;
  };

  // Fetch FEV1 trends from backend
  const { data: trendsResponse, isLoading, isError } = useFEV1Trends(
    parseInt(patientId),
    getWeeks(timeRange)
  );

  // Transform backend data to chart format
  const fev1Data = trendsResponse?.data.map((item, index) => ({
    week: index + 1,
    actual: item.actual || 0,
    predicted: item.predicted,
    mcidLower: item.lowerLimit,
    mcidUpper: item.upperLimit,
    zScore: item.zScore,
    change: item.actual ? ((item.actual - (trendsResponse.data[0].actual || item.predicted)) / item.predicted * 100) : 0,
    date: item.date
  })) || [];

  const currentData = fev1Data[fev1Data.length - 1] || {
    actual: 0,
    predicted: 0,
    zScore: 0,
    change: 0
  };
  
  const baseline = fev1Data[0]?.actual || currentData.predicted;

  const getZScoreStatus = (zScore: number) => {
    if (zScore >= -1.64 && zScore <= 1.64) {
      return { label: 'Normal', color: 'bg-green-500' };
    }
    return { label: 'Abnormal', color: 'bg-destructive' };
  };

  const zScoreStatus = getZScoreStatus(currentData.zScore);

  // Loading state
  if (isLoading) {
    return (
      <Card className="shadow-lg bg-white">
        <CardHeader className="bg-gradient-to-r from-[#2762AC]/5 to-[#14CAF4]/5">
          <CardTitle className="text-primary flex items-center gap-2">
            <Activity className="w-5 h-5" />
            FEV₁ Trends with Z-Scores & MCID Bands
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading FEV₁ trends...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (isError || !trendsResponse) {
    return (
      <Card className="shadow-lg bg-white">
        <CardHeader className="bg-gradient-to-r from-[#2762AC]/5 to-[#14CAF4]/5">
          <CardTitle className="text-primary flex items-center gap-2">
            <Activity className="w-5 h-5" />
            FEV₁ Trends with Z-Scores & MCID Bands
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">No FEV₁ data available</p>
            <p className="text-xs text-muted-foreground mt-1">Patient hasn't completed any spirometry tests yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg bg-white">
      <CardHeader className="bg-gradient-to-r from-[#2762AC]/5 to-[#14CAF4]/5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-primary flex items-center gap-2">
            <Activity className="w-5 h-5" />
            FEV₁ Trends with Z-Scores & MCID Bands
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Header Section */}
        <div className="grid grid-cols-4 gap-2 md:gap-4">
          {/* Current FEV1 */}
          <div className="p-2 md:p-4 bg-white border-2 border-[#2762AC] rounded-xl shadow-sm">
            <p className="text-[10px] md:text-xs text-[#494949] mb-1">Current FEV₁</p>
            <p className="text-[#2762AC] text-lg md:text-2xl">{currentData.actual.toFixed(1)}%</p>
            <p className="text-[10px] md:text-xs text-[#494949] mt-1">{trendsResponse.summary.total_tests} tests</p>
          </div>

          {/* Predicted */}
          <div className="p-2 md:p-4 bg-white border-2 border-[#F07D13] rounded-xl shadow-sm">
            <p className="text-[10px] md:text-xs text-[#494949] mb-1">Predicted</p>
            <p className="text-[#F07D13] text-lg md:text-2xl">{currentData.predicted.toFixed(1)}%</p>
            <p className="text-[10px] md:text-xs text-[#494949] mt-1">GLI-2022</p>
          </div>

          {/* Z-Score */}
          <div className="p-2 md:p-4 bg-white border-2 border-[#14CAF4] rounded-xl shadow-sm">
            <p className="text-[10px] md:text-xs text-[#494949] mb-1">Z-Score</p>
            <p className="text-[#14CAF4] text-lg md:text-2xl">{currentData.zScore.toFixed(1)}</p>
            <p className="text-[10px] md:text-xs text-[#494949] mt-1">{zScoreStatus.label}</p>
          </div>

          {/* Change */}
          <div className="p-2 md:p-4 bg-white border-2 border-[#494949] rounded-xl shadow-sm">
            <p className="text-[10px] md:text-xs text-[#494949] mb-1">Change</p>
            <p className="text-[#494949] text-lg md:text-2xl">{currentData.change > 0 ? '+' : ''}{currentData.change.toFixed(1)}%</p>
            <p className="text-[10px] md:text-xs text-[#494949] mt-1">vs baseline</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Time Range:</span>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6weeks">6 Weeks</SelectItem>
                <SelectItem value="12weeks">12 Weeks</SelectItem>
                <SelectItem value="6months">6 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="border-[#F07D13] text-[#F07D13] hover:bg-[#F07D13]/10">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Line Chart */}
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={fev1Data}>
              <defs>
                <linearGradient id="fev1Gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2762AC" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#2762AC" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
              <XAxis 
                dataKey="week" 
                stroke="#494949"
                tick={{ fontSize: 12 }}
                label={{ value: 'Week', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
              />
              <YAxis 
                stroke="#494949"
                domain={['auto', 'auto']}
                tick={{ fontSize: 12 }}
                label={{ value: 'FEV₁ (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E5E5E3',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '12px'
                }}
                formatter={(value: any, name: string) => {
                  if (name === 'actual') return [`${value}%`, 'Actual FEV₁'];
                  if (name === 'predicted') return [`${value}%`, 'Predicted'];
                  if (name === 'mcidLower') return [`${value}%`, 'MCID Lower'];
                  if (name === 'mcidUpper') return [`${value}%`, 'MCID Upper'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Week ${label}`}
              />
              <ReferenceLine 
                y={fev1Data[0]?.mcidLower || 0} 
                stroke="#9CA3AF" 
                strokeDasharray="5 5" 
                label={{ value: 'MCID Lower', position: 'insideTopLeft', fontSize: 10, fill: '#9CA3AF' }} 
              />
              <ReferenceLine 
                y={fev1Data[0]?.mcidUpper || 0} 
                stroke="#9CA3AF" 
                strokeDasharray="5 5" 
                label={{ value: 'MCID Upper', position: 'insideTopLeft', fontSize: 10, fill: '#9CA3AF' }} 
              />
              <Area
                type="monotone"
                dataKey="actual"
                stroke="#2762AC"
                strokeWidth={2}
                fill="url(#fev1Gradient)"
                dot={{ fill: '#2762AC', r: 5, strokeWidth: 2, stroke: 'white' }}
                name="actual"
              />
              <Line
                type="monotone"
                dataKey="predicted"
                stroke="#F07D13"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="predicted"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-6 p-4 bg-[#F5F5F5] rounded-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-[#2762AC] rounded"></div>
            <span className="text-sm text-[#494949]">Actual FEV₁</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 border-t-2 border-dashed border-[#F07D13]"></div>
            <span className="text-sm text-[#494949]">Predicted (GLI-2022)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 border-t-2 border-dashed border-[#9CA3AF]"></div>
            <span className="text-sm text-[#494949]">MCID Lower Limit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 border-t-2 border-dashed border-[#9CA3AF]"></div>
            <span className="text-sm text-[#494949]">MCID Upper Limit</span>
          </div>
        </div>

        {/* Z-Score Interpretation Bar */}
        <div className="p-4 bg-gradient-to-r from-[#E5E5E3] to-[#F5F5F5] rounded-xl border border-[#E5E5E3]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm mb-2 text-[#494949]">Z-Score Interpretation</p>
              <p className="text-[#494949] text-xs">
                Normal Range: <span className="text-[#2762AC]">-1.64 to +1.64</span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-[#494949]">Current Z-Score</p>
                <p className="text-[#2762AC]">{currentData.zScore.toFixed(2)}</p>
              </div>
              <Badge className={`${zScoreStatus.color} text-white`}>
                {zScoreStatus.label}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}